import { isApprovalFlowsEnabled } from '@/services/systemConfiguration.utilities';
import { useGetWorkbookQuery } from '@seeqdev/api/graphql/GetWorkbook';
import { useGetWorkbookWithApprovalFlowQuery } from '@seeqdev/api/graphql/GetWorkbookWithApprovalFlow';
import type { RefetchOptions } from '@tanstack/react-query';
import type { WorkbookQueryResult } from '@/worksheet/worksheet.types';

interface UseGetWorkbookParams {
  workbookId?: string;
  worksheetId?: string;
  workstepId?: string;
  skipWorksheets?: boolean;
}

/**
 * Fetches from whichever workbook operation matches the approval-flow feature flag. Both operations spread the same
 * `WorkbookCore` fragment, so callers see a single shape — `WorkbookQueryResult`, whose approval fields are optional
 * because only `GetWorkbookWithApprovalFlow` selects them.
 */
export type WorkbookFetch = (options?: RefetchOptions) => Promise<{ data: WorkbookQueryResult | undefined }>;

/**
 * This hook gives you a stable function reference based on the passed in parameters to fetch workbook data.
 */
export const useGetWorkbook = ({
  workbookId,
  worksheetId,
  workstepId,
  skipWorksheets = false,
}: UseGetWorkbookParams): { fetch: WorkbookFetch } => {
  // enabled: false stops React Query from pre-fetching the request, which means it will not be fetched for you.
  // refetch lets you manually request the data using the most up-to-date parameters based on what you pass into the
  // query. This keeps the hook as the source of truth while allowing you to delay fetching until you know you need it.

  // WARNING: YOU PROBABLY SHOULDN'T BE USING THE GENERATED REACT QUERY HOOKS LIKE THIS. Most of the time you want
  // `enabled: true` and `retry: true` (the defaults). Fetching workbooks cannot rely on the cached response due
  // to worksteps and the fetching must be controlled due to the current loading pattern. If you are using another
  // generated React Query hook, simply use it inline with the defaults. I REPEAT, YOU PROBABLY DON'T NEED TO DO
  // WHAT THIS HOOK USES.

  // Both operations take the same variables and spread the same `WorkbookCore` fragment; the only difference is that
  // GetWorkbookWithApprovalFlow also spreads `ApprovalFlowSection`.
  const variables = {
    workbookId: workbookId ?? '',
    worksheetId: worksheetId ?? '',
    workstepId,
    skipWorksheets,
  };
  const { refetch: coreRefetch } = useGetWorkbookQuery(variables, { enabled: false, retry: false });
  const { refetch: withApprovalRefetch } = useGetWorkbookWithApprovalFlowQuery(variables, {
    enabled: false,
    retry: false,
  });

  // The choice must not depend on anything that changes while a workbook loads (like sqWorkbookStore.type): the
  // returned fetch sits in load-effect dependency arrays, so an identity change mid-load re-triggers the load
  // (CRAB-63741). isApprovalFlowsEnabled() is deployment configuration, so it is stable for the life of the page.
  // Returning refetch directly — no wrapper, no useCallback — keeps that identity react-query's own.
  return { fetch: isApprovalFlowsEnabled() ? withApprovalRefetch : coreRefetch };
};
